import { memo, useMemo } from "react";
import { View } from "react-native";
import usePalette from "src/hooks/usePalette";
import useStyle from "src/hooks/useStyle";
import { color } from "src/styles";
import { paddingLarge } from "src/styles/spacing";
import CommentAttachmentDeletedThread from "./components/CommentAttachmentDeletedThread";
import CommentAttachmentLargeImage from "./components/CommentAttachmentLargeImage";
import CommentAttachmentName from "./components/CommentAttachmentName";
import CommentAttachmentProfilePicture from "./components/CommentAttachmentProfilePicture";
import CommentAttachmentReplyCount from "./components/CommentAttachmentReplyCount";
import CommentAttachmentSmallImage from "./components/CommentAttachmentSmallImage";
import CommentBubbleText from "./components/CommentBubbleText";
import CommentMessageUnsent from "./components/CommentMessageUnsent";
import {
	CommentBubbleMarginSize,
	CommentBubbleRepliesAttachmentHeight,
	CommentSmallImageHeightBoundingBox,
} from "./constants";
import { CommentBubbleProps } from "./types";

const CommentBubble = ({
	content,
	position = "Top",
	sender = "Other",
	isSending = false,
	attachments = [],
}: CommentBubbleProps) => {
	const { primary, isFallback } = usePalette();
	const { theme } = useStyle();
	const actionColor = isFallback ? color.Action[theme] : primary;
	const backgroundColor = sender === "Self" ? actionColor : color.BackgroundOtherMessage[theme];

	const {
		nameAttachment,
		profilePictureAttachment,
		largeImageAttachment,
		smallImageAttachment,
		replyCountAttachment,
		deletedThreadAttachment,
		contextMenuAttachment,
	} = useMemo(() => {
		// Intentionally only grabbing one of each attachment
		// There's no attachments that should stack with multiple
		return {
			nameAttachment: attachments.find((attachment) => attachment.variant === "Name"),
			profilePictureAttachment:
				sender === "Other"
					? attachments.find((attachment) => attachment.variant === "ProfilePicture")
					: undefined,
			largeImageAttachment: attachments.find(
				(attachment) => attachment.variant === "LargeImage",
			),
			smallImageAttachment: attachments.find(
				(attachment) => attachment.variant === "SmallImage",
			),
			replyCountAttachment: attachments.find(
				(attachment) => attachment.variant === "ReplyCount",
			),
			deletedThreadAttachment: attachments.find(
				(attachment) => attachment.variant === "DeletedThread",
			),
			contextMenuAttachment: attachments.find(
				(attachment) => attachment.variant === "ContextMenu",
			),
		};
	}, [attachments, sender]);

	return (
		<View
			testID="CommentBubble"
			style={[
				{
					width: "100%",
					paddingHorizontal: sender === "Other" ? CommentBubbleMarginSize : paddingLarge,
					marginTop: position === "Top" ? paddingLarge : 3,
					paddingTop: !!smallImageAttachment ? CommentSmallImageHeightBoundingBox : 0,
				},
				sender === "Other" && {
					alignItems: "flex-start",
				},
				sender === "Self" && {
					alignItems: "flex-end",
				},
			]}
		>
			{!!profilePictureAttachment && (
				<CommentAttachmentProfilePicture
					attachment={profilePictureAttachment}
					sender={sender}
					bottom={replyCountAttachment ? CommentBubbleRepliesAttachmentHeight : 0}
				/>
			)}
			{!!nameAttachment && <CommentAttachmentName attachment={nameAttachment} />}
			{!!largeImageAttachment && (
				<CommentAttachmentLargeImage attachment={largeImageAttachment} />
			)}
			{!!deletedThreadAttachment && (
				<CommentAttachmentDeletedThread attachment={deletedThreadAttachment} />
			)}
			<View
				style={{
					maxWidth: 285,
				}}
			>
				{content != null ? (
					<>
						{!!smallImageAttachment && (
							<CommentAttachmentSmallImage
								attachment={smallImageAttachment}
								sender={sender}
							/>
						)}
						<CommentBubbleText
							content={content}
							backgroundColor={backgroundColor}
							position={position}
							sender={sender}
							isSending={isSending}
							contextMenuAttachment={contextMenuAttachment}
						/>
					</>
				) : (
					<CommentMessageUnsent />
				)}
			</View>
			{!!replyCountAttachment && (
				<CommentAttachmentReplyCount
					attachment={replyCountAttachment}
					color={actionColor}
				/>
			)}
		</View>
	);
};
export default memo(CommentBubble);
